/**
 * feat-300 — the reply box: textarea, Speak / Add by voice, Send, and the
 * recorder sub-states (design NOTES §3.4, §3.5).
 *
 * The draft text is owned by the panel and NEVER derived from a poll, so a
 * poll cannot re-render a focused textarea's value (NOTES §5).
 */
import React from 'react';
import { Mic, MicOff, Send } from 'lucide-react';
import { WorkingSpinner } from './MicButton';
import { MAX_SECONDS, Recorder } from './useRecorder';
import {
  BTN_PRIMARY,
  BTN_QUIET,
  BTN_SECONDARY,
  COMPOSER,
  HINT,
  KBD,
  NOTE_DANGER,
  NOTE_INFO,
  REC_BAR,
  REC_FILL,
  REC_TRACK,
  ROW,
  TEXTAREA,
} from './classes';
import { QUESTION_EDIT_COPY as COPY } from './voiceEditCopy';

/** When to start announcing the time left: 1:45 (NOTES §3.4). */
const WARN_AT = 105;

export interface ComposerProps {
  panelId: string;
  firstTurn: boolean;
  text: string;
  onTextChange: (text: string) => void;
  /** The box holds text from a recording the reviewer has not edited yet. */
  fromRecording: boolean;
  recorder: Recorder;
  onSend: () => void;
  sending: boolean;
  /** A composer-local note (e.g. a 409 turn_in_progress). */
  notice?: React.ReactNode;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  stopRef: React.RefObject<HTMLButtonElement | null>;
  onCancelRecording: () => void;
}

const RecordingDot: React.FC = () => (
  <span className="relative flex w-2.5 h-2.5" aria-hidden>
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-danger opacity-60 motion-reduce:hidden" />
    <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-danger" />
  </span>
);

const Composer: React.FC<ComposerProps> = ({
  panelId,
  firstTurn,
  text,
  onTextChange,
  fromRecording,
  recorder,
  onSend,
  sending,
  notice,
  textareaRef,
  stopRef,
  onCancelRecording,
}) => {
  const boxId = `${panelId}-box`;
  const recording = recorder.phase === 'recording' || recorder.phase === 'starting';
  const transcribing = recorder.phase === 'transcribing';
  const blank = text.trim() === '';
  const sendDisabled = blank || sending || recording || transcribing;
  const m = Math.floor(recorder.elapsed / 60);
  const s = recorder.elapsed % 60;
  // Update every 5 s, not every second, or a screen reader would chatter.
  const left = Math.ceil((MAX_SECONDS - recorder.elapsed) / 5) * 5;

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // ⌘/Ctrl+Enter sends; Enter adds a line; Esc does nothing here — never drop
    // typed text on a stray key.
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (!sendDisabled) onSend();
    }
  };

  const problem = recorder.problem;
  let note: React.ReactNode = null;
  if (problem === 'failed') {
    note = (
      <div className={NOTE_DANGER} role="alert" data-testid="qe-note">
        <MicOff className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden />
        <p>
          <strong className="font-semibold">{COPY.transcribeFailedLead}</strong> {COPY.transcribeFailedTail}{' '}
          <button
            type="button"
            className="font-semibold underline underline-offset-2 hover:no-underline rounded"
            onClick={() => recorder.start()}
          >
            {COPY.recordAgain}
          </button>{' '}
          {COPY.orTypeInstead}
        </p>
      </div>
    );
  } else if (problem) {
    const sentence =
      problem === 'denied'
        ? COPY.micDenied
        : problem === 'unavailable'
          ? COPY.micUnavailable
          : problem === 'nothing'
            ? COPY.nothingHeard
            : problem === 'tooLong'
              ? COPY.transcribeErr.tooLong
              : COPY.transcribeErr.unsupported;
    note = (
      <div className={NOTE_INFO} role="status" data-testid="qe-note" data-problem={problem}>
        <MicOff className="w-3.5 h-3.5 shrink-0 mt-px text-inkMuted" aria-hidden />
        <p>{sentence}</p>
      </div>
    );
  }

  return (
    // Security M3: the transcript/reply box and everything around it is masked in
    // Replay (the textarea value is also covered by Replay's maskAllInputs).
    <div data-sentry-mask className={COMPOSER} data-testid="qe-composer">
      {(recording || transcribing) && (
        <div
          className={REC_BAR}
          data-testid="qe-recbar"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              onCancelRecording();
            }
          }}
        >
          {transcribing ? (
            <>
              <WorkingSpinner />
              <span className="text-sm font-semibold text-ink">{COPY.transcribing}</span>
              <button type="button" className={`${BTN_QUIET} ml-auto`} onClick={onCancelRecording}>
                {COPY.cancel}
              </button>
            </>
          ) : (
            <>
              <RecordingDot />
              <span className="text-sm font-semibold text-ink">{COPY.recording}</span>
              <span className="text-sm text-ink" aria-label={COPY.recordingAria(m, s)}>
                {COPY.recordingTime(m, s)}
                <span className="text-inkMuted">{COPY.recordingCap}</span>
              </span>
              <span className={REC_TRACK} aria-hidden>
                <span
                  className={`block ${REC_FILL}`}
                  style={{ width: `${(Math.min(recorder.elapsed, MAX_SECONDS) / MAX_SECONDS) * 100}%` }}
                />
              </span>
              {recorder.elapsed >= WARN_AT && (
                <span className="text-xs font-semibold text-attention-on" aria-live="polite">
                  {COPY.secondsLeft(left)}
                </span>
              )}
              <button type="button" className={BTN_QUIET} onClick={onCancelRecording}>
                {COPY.cancel}
              </button>
              <button
                ref={stopRef}
                type="button"
                className={BTN_SECONDARY}
                onClick={recorder.stop}
                disabled={recorder.phase === 'starting'}
              >
                <span className="inline-block w-3 h-3 rounded-[2px] bg-ink" aria-hidden />
                {COPY.stop}
              </button>
            </>
          )}
        </div>
      )}
      {recording && <p className={`mt-1 ${HINT}`}>{COPY.recHint}</p>}
      {recorder.capStopped && !recording && <p className={`mt-1 mb-1 ${HINT}`}>{COPY.capStopped}</p>}

      <label htmlFor={boxId} className="sr-only">
        {firstTurn ? COPY.boxLabelFirst : COPY.boxLabelReply}
      </label>
      <textarea
        id={boxId}
        ref={textareaRef}
        className={`${TEXTAREA}${recording || transcribing ? ' mt-2' : ''}`}
        style={{ fieldSizing: 'content' } as React.CSSProperties}
        placeholder={firstTurn ? COPY.placeholderFirst : COPY.placeholderReply}
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
      {fromRecording && (
        <p className={`mt-1 ${HINT}`}>
          <Mic className="w-3 h-3 inline" aria-hidden /> {COPY.fromRecording}
        </p>
      )}
      {note}
      {notice}

      <div className={ROW}>
        {!recording && !transcribing && (
          <button type="button" className={BTN_SECONDARY} onClick={() => recorder.start()}>
            <Mic className="w-3.5 h-3.5" aria-hidden />
            {blank ? COPY.speak : COPY.addByVoice}
          </button>
        )}
        <span className={`${HINT} ml-auto inline-flex items-center gap-1 [@media(pointer:coarse)]:hidden`}>
          <kbd className={KBD}>⌘/Ctrl</kbd>
          <kbd className={KBD}>Enter</kbd>
          {COPY.sendHint}
        </span>
        <button
          type="button"
          className={BTN_PRIMARY}
          onClick={onSend}
          disabled={sendDisabled}
          aria-disabled={sendDisabled}
        >
          {sending ? <WorkingSpinner /> : <Send className="w-3.5 h-3.5" aria-hidden />}
          {firstTurn ? COPY.send : COPY.sendReply}
        </button>
      </div>
    </div>
  );
};

export default Composer;
